import React, { useMemo, useState } from 'react'
import './ReaderAnalyticalWorkPackage.css'
import { CHARACTER_ANALYTICAL_DIMENSIONS } from '../../lib/analyticalTasks'
import type {
  AnalyticalDimension,
  AnalyticalPosition,
  AnalyticalTaskSet,
  AnalyticalTaskState,
} from '../../types/analyticalTaskTypes'

interface ReaderAnalyticalWorkPackageProps {
  taskSet: AnalyticalTaskSet
  characterId: string
  witnessId: string
  currentPosition: AnalyticalPosition
  onSetWorkstreamState: (workstreamId: string, state: AnalyticalTaskState) => void | Promise<void>
  onSetCheckpointState: (checkpointId: string, state: AnalyticalTaskState) => void | Promise<void>
  onCreateCheckpoint: (request: {
    title?: string
    workstreamIds: string[]
    scope: {
      chapterId: string
      witnessId: string
      start: AnalyticalPosition
      end?: AnalyticalPosition
      resume: AnalyticalPosition
    }
  }) => void | Promise<void>
  onResumePosition: (position: AnalyticalPosition) => void
}

const DIMENSION_LABELS: Record<string, string> = {
  identity: 'Identity',
  coreference: 'Coreference',
  characterization: 'Characterization',
  focalization: 'Focalization',
  speaker: 'Speaker',
  review: 'Review',
}

const stateLabel = (state: AnalyticalTaskState): string => state.replace('_', ' ')

export const ReaderAnalyticalWorkPackage: React.FC<ReaderAnalyticalWorkPackageProps> = ({
  taskSet,
  characterId,
  witnessId,
  currentPosition,
  onSetWorkstreamState,
  onSetCheckpointState,
  onCreateCheckpoint,
  onResumePosition,
}) => {
  const subjectId = `character:${characterId}`
  const subject = taskSet.subjects.find(candidate => candidate.id === subjectId && !candidate.archivedAt)
  const workstreams = useMemo(() => taskSet.workstreams
    .filter(stream => stream.subjectId === subjectId && stream.witnessId === witnessId && !stream.archivedAt)
    .slice()
    .sort((left, right) => left.order - right.order), [subjectId, taskSet.workstreams, witnessId])
  const candidateSubjects = useMemo(() => taskSet.subjects
    .filter(candidate => candidate.kind === 'character' && !candidate.archivedAt && taskSet.workstreams.some(
      stream => stream.subjectId === candidate.id && stream.witnessId === witnessId && !stream.archivedAt,
    ))
    .sort((left, right) => left.createdAt.localeCompare(right.createdAt)), [taskSet.subjects, taskSet.workstreams, witnessId])
  const checkpoints = useMemo(() => taskSet.checkpoints
    .filter(checkpoint => checkpoint.subjectIds.includes(subjectId) && checkpoint.scope.witnessId === witnessId)
    .slice()
    .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt)), [subjectId, taskSet.checkpoints, witnessId])
  const [composing, setComposing] = useState(false)
  const [title, setTitle] = useState('')
  const [selectedSubjectIds, setSelectedSubjectIds] = useState<string[]>([subjectId])
  const [selectedDimensions, setSelectedDimensions] = useState<AnalyticalDimension[]>(['coreference'])
  const [start, setStart] = useState<AnalyticalPosition>(currentPosition)
  const [end, setEnd] = useState<AnalyticalPosition | undefined>()
  const [pendingKey, setPendingKey] = useState('')
  const [error, setError] = useState('')

  if (!subject || workstreams.length === 0) return null

  const completed = workstreams.filter(stream => stream.state === 'done').length
  const chapterId = taskSet.enrollments.find(enrollment => enrollment.witnessId === witnessId)?.chapterId ||
    workstreams[0].chapterId

  const run = async (key: string, operation: () => void | Promise<void>) => {
    setPendingKey(key)
    setError('')
    try {
      await operation()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The analytical task could not be updated.')
    } finally {
      setPendingKey('')
    }
  }

  const toggleSubject = (candidateId: string) => {
    setSelectedSubjectIds(current => current.includes(candidateId)
      ? current.filter(id => id !== candidateId)
      : [...current, candidateId])
  }

  const toggleDimension = (dimension: AnalyticalDimension) => {
    setSelectedDimensions(current => current.includes(dimension)
      ? current.filter(value => value !== dimension)
      : [...current, dimension])
  }

  const openComposer = () => {
    setTitle('')
    setSelectedSubjectIds([subjectId])
    setSelectedDimensions(['coreference'])
    setStart({ ...currentPosition })
    setEnd(undefined)
    setError('')
    setComposing(true)
  }

  const saveCheckpoint = () => {
    const workstreamIds = taskSet.workstreams.filter(stream => (
      selectedSubjectIds.includes(stream.subjectId) &&
      selectedDimensions.includes(stream.dimension) &&
      stream.witnessId === witnessId &&
      !stream.archivedAt
    )).map(stream => stream.id)
    if (workstreamIds.length === 0) {
      setError('Choose at least one character and one analytical layer.')
      return
    }
    void run('create-checkpoint', async () => {
      await onCreateCheckpoint({
        ...(title.trim() ? { title: title.trim() } : {}),
        workstreamIds,
        scope: {
          chapterId,
          witnessId,
          start: { ...start },
          ...(end ? { end: { ...end } } : {}),
          resume: { ...currentPosition },
        },
      })
      setComposing(false)
    })
  }

  return (
    <section
      className="lectio-analytical-package"
      aria-label={`${subject.label} analytical work package`}
    >
      <header className="lectio-analytical-package-head">
        <span>Work package</span>
        <b>{completed}/{workstreams.length} done</b>
      </header>

      <div className="lectio-analytical-streams">
        {workstreams.map(stream => (
          <div
            key={stream.id}
            className="lectio-analytical-stream"
            data-testid={`analytical-workstream-${stream.dimension}`}
            data-state={stream.state}
          >
            <div>
              <b>{DIMENSION_LABELS[stream.dimension] || stream.dimension}</b>
              <span>{stateLabel(stream.state)}</span>
              {stream.lastPosition?.unitLabel && <small>{stream.lastPosition.unitLabel}</small>}
            </div>
            <nav aria-label={`${stream.dimension} status actions`}>
              {stream.state === 'not_started' && (
                <button
                  type="button"
                  disabled={Boolean(pendingKey)}
                  aria-label={`Start ${stream.dimension}`}
                  onClick={() => void run(stream.id, () => onSetWorkstreamState(stream.id, 'in_progress'))}
                >start</button>
              )}
              {stream.state === 'in_progress' && <>
                <button
                  type="button"
                  disabled={Boolean(pendingKey)}
                  aria-label={`Send ${stream.dimension} to review`}
                  onClick={() => void run(stream.id, () => onSetWorkstreamState(stream.id, 'in_review'))}
                >review</button>
                <button
                  type="button"
                  disabled={Boolean(pendingKey)}
                  aria-label={`Mark ${stream.dimension} done`}
                  onClick={() => void run(stream.id, () => onSetWorkstreamState(stream.id, 'done'))}
                >done</button>
              </>}
              {stream.state === 'in_review' && <>
                <button
                  type="button"
                  disabled={Boolean(pendingKey)}
                  aria-label={`Resume ${stream.dimension}`}
                  onClick={() => void run(stream.id, () => onSetWorkstreamState(stream.id, 'in_progress'))}
                >resume</button>
                <button
                  type="button"
                  disabled={Boolean(pendingKey)}
                  aria-label={`Mark ${stream.dimension} done`}
                  onClick={() => void run(stream.id, () => onSetWorkstreamState(stream.id, 'done'))}
                >done</button>
              </>}
              {stream.state === 'done' && (
                <button
                  type="button"
                  disabled={Boolean(pendingKey)}
                  aria-label={`Reopen ${stream.dimension}`}
                  onClick={() => void run(stream.id, () => onSetWorkstreamState(stream.id, 'in_progress'))}
                >reopen</button>
              )}
            </nav>
          </div>
        ))}
      </div>

      <div className="lectio-analytical-checkpoints">
        <div className="lectio-analytical-subhead">
          <span>Checkpoints</span>
          <button type="button" aria-label="Create checkpoint" onClick={openComposer}>+ checkpoint</button>
        </div>
        {checkpoints.length === 0 ? (
          <p>No checkpoint for this figure</p>
        ) : checkpoints.slice(0, 3).map(checkpoint => (
          <article key={checkpoint.id} data-state={checkpoint.state}>
            <button
              type="button"
              className="lectio-analytical-resume"
              aria-label={`Resume checkpoint at ${checkpoint.scope.resume.unitLabel || checkpoint.scope.resume.rowId}`}
              onClick={() => onResumePosition(checkpoint.scope.resume)}
            >
              <b>{checkpoint.title || checkpoint.dimensions.map(dimension => DIMENSION_LABELS[dimension] || dimension).join(' + ')}</b>
              <span>{checkpoint.scope.start.unitLabel || checkpoint.scope.start.rowId}{checkpoint.scope.end
                ? `–${checkpoint.scope.end.unitLabel || checkpoint.scope.end.rowId}`
                : ' →'}</span>
            </button>
            <small>{stateLabel(checkpoint.state)}</small>
            <nav aria-label={`${checkpoint.title || checkpoint.id} checkpoint status actions`}>
              {checkpoint.state === 'in_progress' && <>
                <button
                  type="button"
                  onClick={() => void run(checkpoint.id, () => onSetCheckpointState(checkpoint.id, 'in_review'))}
                >review</button>
                <button
                  type="button"
                  onClick={() => void run(checkpoint.id, () => onSetCheckpointState(checkpoint.id, 'done'))}
                >done</button>
              </>}
              {(checkpoint.state === 'in_review' || checkpoint.state === 'done') && (
                <button
                  type="button"
                  onClick={() => void run(checkpoint.id, () => onSetCheckpointState(checkpoint.id, 'in_progress'))}
                >resume</button>
              )}
            </nav>
          </article>
        ))}
      </div>

      {composing && (
        <div className="lectio-analytical-composer" role="group" aria-label="New analytical checkpoint">
          <label>
            <span>Checkpoint title</span>
            <input
              aria-label="Checkpoint title"
              value={title}
              onChange={event => setTitle(event.target.value)}
              placeholder="Passage or problem"
            />
          </label>
          <fieldset>
            <legend>Characters</legend>
            {candidateSubjects.map(candidate => (
              <label key={candidate.id}>
                <input
                  type="checkbox"
                  aria-label={`${candidate.label} subject`}
                  checked={selectedSubjectIds.includes(candidate.id)}
                  onChange={() => toggleSubject(candidate.id)}
                />
                <span>{candidate.label}</span>
              </label>
            ))}
          </fieldset>
          <fieldset>
            <legend>Layers</legend>
            {CHARACTER_ANALYTICAL_DIMENSIONS.map(dimension => (
              <label key={dimension}>
                <input
                  type="checkbox"
                  aria-label={`${dimension} layer`}
                  checked={selectedDimensions.includes(dimension)}
                  onChange={() => toggleDimension(dimension)}
                />
                <span>{DIMENSION_LABELS[dimension] || dimension}</span>
              </label>
            ))}
          </fieldset>
          <div className="lectio-analytical-scope">
            <span>Scope</span>
            <b>{start.unitLabel || start.rowId}{end ? `–${end.unitLabel || end.rowId}` : ' → open'}</b>
            <button type="button" onClick={() => setStart({ ...currentPosition })}>start here</button>
            <button type="button" onClick={() => setEnd(end ? undefined : { ...currentPosition })}>
              {end ? 'open end' : 'end here'}
            </button>
          </div>
          <div className="lectio-analytical-composer-actions">
            <button type="button" aria-label="Save checkpoint" disabled={Boolean(pendingKey)} onClick={saveCheckpoint}>save checkpoint</button>
            <button type="button" disabled={Boolean(pendingKey)} onClick={() => setComposing(false)}>cancel</button>
          </div>
        </div>
      )}
      {error && <p className="lectio-analytical-error" role="alert">{error}</p>}
    </section>
  )
}
